import { useRef, useState } from 'react';
import type { Plugin, PluginFunctions } from '@react-pdf-viewer/core';

const THRESHOLD = 10;

const isScrollable = (el: HTMLElement) => el.scrollHeight - el.clientHeight > 2;

/**
 * The element that actually scrolls may be the PDF pages container OR one of its
 * ancestors (the MUI dialog content), depending on layout. Walk from the pages
 * container up to <body> and pick the scrollable element that has scrolled the
 * most — that's the one the user is driving.
 */
const findActiveScroller = (start: HTMLElement | null): HTMLElement | null => {
  let best: HTMLElement | null = null;
  let node: HTMLElement | null = start;
  while (node && node !== document.body) {
    if (isScrollable(node) && (!best || node.scrollTop > best.scrollTop)) {
      best = node;
    }
    node = node.parentElement;
  }
  return best;
};

type UseScrollDetectionPluginReturn = {
  plugin: Plugin;
  hasScrolledToBottom: boolean;
  reset: () => void;
};

export const useScrollDetectionPlugin = (): UseScrollDetectionPluginReturn => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const containerGetterRef = useRef<(() => HTMLElement | null) | null>(null);
  const scrollerRef = useRef<HTMLElement | null>(null);
  const docListenerRef = useRef<(() => void) | null>(null);
  const pollRef = useRef<(() => void) | null>(null);
  const rafRef = useRef<number | null>(null);
  const reachedBottomRef = useRef(false);

  const startPolling = () => {
    if (rafRef.current != null) return;
    const poll = pollRef.current;
    if (poll) rafRef.current = requestAnimationFrame(poll);
  };

  const pluginRef = useRef<Plugin>({
    install(pluginFunctions: PluginFunctions) {
      containerGetterRef.current = () => pluginFunctions.getPagesContainer();

      const check = () => {
        if (reachedBottomRef.current) return;
        const container = containerGetterRef.current?.() ?? null;
        const scroller = findActiveScroller(container);
        if (!scroller) {
          // Container mounted but no ancestor is scrollable — content fits in the
          // viewport without scrolling. Treat this as "reached bottom" so the user
          // is not permanently blocked from accepting. Guard on container != null
          // to avoid firing before the PDF pages element has mounted.
          if (container) {
            reachedBottomRef.current = true;
            setHasScrolledToBottom(true);
          }
          return;
        }
        const remaining = scroller.scrollHeight - (scroller.scrollTop + scroller.clientHeight);
        if (remaining <= THRESHOLD) {
          reachedBottomRef.current = true;
          setHasScrolledToBottom(true);
        }
      };

      // Listen at document level with capture so we catch scroll events on ANY element
      // in the subtree — the actual scrolling element varies by host layout and cannot
      // be reliably predicted by walking the DOM at install time.
      const handleScroll = () => check();
      docListenerRef.current = handleScroll;
      document.addEventListener('scroll', handleScroll, { capture: true, passive: true });

      // Check once synchronously on mount — handles content that fits in the
      // viewport without scrolling (short PDF, large display, zoomed-out browser).
      check();

      // rAF poll: waits until findActiveScroller finds the real scroller, then does a
      // one-shot check in case the user already scrolled before the listener could fire.
      const poll = () => {
        rafRef.current = null;
        if (reachedBottomRef.current) return;
        const container = containerGetterRef.current?.() ?? null;
        const scroller = findActiveScroller(container);
        if (!scroller) {
          rafRef.current = requestAnimationFrame(poll);
          return;
        }
        scrollerRef.current = scroller;
        check();
      };
      pollRef.current = poll;
      startPolling();
    },

    uninstall() {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      if (docListenerRef.current) {
        document.removeEventListener('scroll', docListenerRef.current, true);
        docListenerRef.current = null;
      }
      scrollerRef.current = null;
      containerGetterRef.current = null;
      pollRef.current = null;
    },
  });

  const reset = (): void => {
    reachedBottomRef.current = false;
    setHasScrolledToBottom(false);
    // Return the scroller to the top so the dialog reopens at the start.
    const el = scrollerRef.current ?? containerGetterRef.current?.() ?? null;
    if (el) el.scrollTop = 0;
    startPolling();
  };

  return { plugin: pluginRef.current, hasScrolledToBottom, reset };
};
